/**
 * 验证来源信息格式是否正确
 * @param {Object} sourceInfo 来源信息对象
 * @returns {boolean} 是否有效
 */
export function isValidSourceInfo(sourceInfo) {
  return sourceInfo && 
         typeof sourceInfo === 'object' && 
         ['utm', 'referrer', 'direct'].includes(sourceInfo.type) &&
         typeof sourceInfo.source === 'string';
}

/**
 * 获取用户来源（utm_source 优先，其次 referrer）
 * @returns {Object} 来源信息对象
 */
function detectSource() {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm');

  if (utmSource) {
    return { type: 'utm', source: utmSource };
  }

  const referrer = document.referrer;
  if (referrer) {
    try {
      const refUrl = new URL(referrer);
      const hostname = refUrl.hostname.toLowerCase();

      // 常见来源映射（使用对象映射可以更灵活地控制返回值）
      const knownSources = {
        'google.com': 'google',
        'baidu.com': 'baidu',
        'bing.com': 'bing',
        'facebook.com': 'facebook',
        'twitter.com': 'twitter',
        'linkedin.com': 'linkedin',
        'weibo.com': 'weibo',
        'wechat.com': 'wechat',
        'mail.qq.com': 'qq_mail',
        'mail.163.com': 'netease_mail',
        'zhihu.com': 'zhihu',
        'toutiao.com': 'toutiao',
        'jd.com': 'jd',
        'taobao.com': 'taobao',
        'tmall.com': 'tmall',
        'm.baidu.com': 'baidu_mobile', // 移动端来源特殊处理
        'bing.com/search': 'bing_search' // 支持路径匹配
      };

      // 检查精确匹配
      for (const [domain, sourceName] of Object.entries(knownSources)) {
        if (hostname.includes(domain) || (refUrl.href && refUrl.href.includes(domain))) {
          return { type: 'referrer', source: sourceName };
        }
      }

      // 返回泛化域名（如 example.com → example）
      const mainDomain = hostname.split('.').slice(-2).join('.');
      return { type: 'referrer', source: mainDomain };
    } catch (e) {
      console.warn('解析 referrer 失败:', referrer);
    }
  }

  return { type: 'direct', source: 'direct' };
}

/**
 * 获取存储的用户来源信息
 * @returns {Object|null} 来源信息对象或null
 */
export function getUserSource() {
  try {
    const stored = localStorage.getItem('user_source');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn('解析用户来源信息失败:', e);
    return null;
  }
}

/**
 * 获取并存储用户来源信息
 * @param {boolean} forceUpdate - 是否强制更新来源信息
 * @returns {Object} 存储的来源信息对象
 */
export function captureAndStoreUserSource(forceUpdate = false) {
  // 检查是否已存在存储的来源信息且不强制更新
  if (!forceUpdate) {
    const existingSource = localStorage.getItem('user_source');
    if (existingSource) {
      try {
        return JSON.parse(existingSource);
      } catch (e) {
        console.warn('解析现有来源信息失败，将重新获取:', e);
      }
    }
  }
  
  // 获取来源信息
  const sourceInfo = detectSource();
  
  // 添加时间戳和版本信息
  const sourceWithMeta = {
    ...sourceInfo,
    captured_at: Date.now(),
    version: '1.0'
  };
  
  const sourceJson = JSON.stringify(sourceWithMeta);
  
  // 使用localStorage存储
  try {
    localStorage.setItem('user_source', sourceJson);
    console.log('已记录用户来源:', sourceInfo);
  } catch (e) {
    console.error('存储用户来源信息失败:', e);
  }
  
  return sourceWithMeta;
}

/**
 * 初始化并保存来源到 localStorage
 * 这个函数应该在应用启动时调用，确保捕获首次访问的来源信息
 */
export function initSourceTracking() {
  captureAndStoreUserSource(false);
}

/**
 * 获取登录时需要传递给后端的来源信息对象（驼峰命名风格）
 * @returns {Object} 包含来源信息的对象，可直接传递给后端接口
 */
export function getLoginSourceData() {
  console.log('getLoginSourceData')
  const sourceInfo = getUserSource();
  
  // 确保来源信息有效，如果无效则使用默认值
  if (!isValidSourceInfo(sourceInfo)) {
    console.log('getLoginSourceData-无效来源信息', sourceInfo);
    return {
      sourceType: 'direct',
      sourceValue: 'direct'
    };
  }
  console.log('getLoginSourceData-有效来源信息', sourceInfo);
  
  // 返回标准化的来源信息对象
  return {
    sourceType: sourceInfo.type,
    sourceValue: sourceInfo.source
  };
}

/**
 * 获取用户来源的简洁描述
 * @returns {string} 来源描述
 */
export function getUserSourceDescription() {
  const sourceInfo = getUserSource();
  if (!sourceInfo) return '未知来源';
  
  const descriptions = {
    utm: '推广链接',
    referrer: '外部引用',
    direct: '直接访问'
  };
  
  const typeDesc = descriptions[sourceInfo.type] || sourceInfo.type;
  return `${typeDesc}: ${sourceInfo.source}`;
}

/**
 * 清理存储的用户来源信息（用于测试或重置）
 */
export function clearUserSource() {
  localStorage.removeItem('user_source');
  console.log('已清理用户来源信息');
}
